import React, { useState, useEffect } from 'react';
import { authenticatedFetch } from '../utils/auth';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    is_admin: false
  });
  const [createLoading, setCreateLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await authenticatedFetch('/admin/users');
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        setError('Failed to fetch users');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      rate_limit_get_digipin: user.rate_limits.get_digipin,
      rate_limit_get_address: user.rate_limits.get_address,
      rate_limit_bulk_csv: user.rate_limits.bulk_csv,
      rate_limit_usage_history: user.rate_limits.usage_history,
      is_admin: user.is_admin
    });
  };

  const handleSaveUser = async () => {
    try {
      const response = await authenticatedFetch(`/admin/users/${editingUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(editForm),
      });

      if (response.ok) {
        setEditingUser(null);
        setEditForm({});
        fetchUsers(); // Refresh the list
      } else {
        setError('Failed to update user');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/admin/users/${userId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        fetchUsers(); // Refresh the list
      } else {
        setError('Failed to delete user');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  const handleRevokeApiKey = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to revoke the API key for ${userEmail}?`)) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/admin/users/${userId}/revoke-api-key`, {
        method: 'DELETE',
      });

      if (response.ok) {
        fetchUsers(); // Refresh the list
      } else {
        setError('Failed to revoke API key');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  const handleCreateUser = async () => {
    if (createForm.password !== createForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!createForm.email || !createForm.password) {
      setError('Email and password are required');
      return;
    }

    setCreateLoading(true);
    setError('');

    try {
      const response = await authenticatedFetch('/admin/users', {
        method: 'POST',
        body: JSON.stringify({
          email: createForm.email,
          password: createForm.password,
          is_admin: createForm.is_admin
        }),
      });

      if (response.ok) {
        setShowCreateUser(false);
        setCreateForm({
          email: '',
          password: '',
          confirmPassword: '',
          is_admin: false
        });
        fetchUsers(); // Refresh the list
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create user');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setCreateLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>User Management</h1>
        <button
          onClick={() => setShowCreateUser(true)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          Create New User
        </button>
      </div>
      
      {error && (
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          backgroundColor: 'white',
          borderRadius: '4px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>ID</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Email</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Admin</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Created</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Last Login</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Rate Limits</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>API Key</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} style={{ borderBottom: '1px solid #f1f1f1' }}>
                <td style={{ padding: '12px' }}>{user.id}</td>
                <td style={{ padding: '12px' }}>{user.email}</td>
                <td style={{ padding: '12px' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    backgroundColor: user.is_admin ? '#d4edda' : '#f8d7da',
                    color: user.is_admin ? '#155724' : '#721c24'
                  }}>
                    {user.is_admin ? 'Admin' : 'User'}
                  </span>
                </td>
                <td style={{ padding: '12px' }}>{formatDate(user.created_at)}</td>
                <td style={{ padding: '12px' }}>{formatDate(user.last_login)}</td>
                <td style={{ padding: '12px' }}>
                  <div style={{ fontSize: '12px' }}>
                    <div>Get Digipin: {user.rate_limits.get_digipin}/min</div>
                    <div>Get Address: {user.rate_limits.get_address}/min</div>
                    <div>Bulk CSV: {user.rate_limits.bulk_csv}/min</div>
                    <div>Usage History: {user.rate_limits.usage_history}/min</div>
                  </div>
                </td>
                <td style={{ padding: '12px' }}>
                  {user.api_key ? (
                    <div>
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        backgroundColor: '#d4edda',
                        color: '#155724'
                      }}>
                        Active
                      </span>
                      <button
                        onClick={() => handleRevokeApiKey(user.id, user.email)}
                        style={{
                          marginLeft: '8px',
                          padding: '2px 6px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '10px'
                        }}
                      >
                        Revoke
                      </button>
                    </div>
                  ) : (
                    <span style={{
                      padding: '2px 6px',
                      borderRadius: '12px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      backgroundColor: '#f8d7da',
                      color: '#721c24'
                    }}>
                      None
                    </span>
                  )}
                </td>
                <td style={{ padding: '12px' }}>
                  <button
                    onClick={() => handleEditUser(user)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginRight: '5px',
                      fontSize: '12px'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editingUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3>Edit User: {editingUser.email}</h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Get Digipin Rate Limit:</label>
              <input
                type="number"
                value={editForm.rate_limit_get_digipin}
                onChange={(e) => setEditForm({...editForm, rate_limit_get_digipin: parseInt(e.target.value)})}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Get Address Rate Limit:</label>
              <input
                type="number"
                value={editForm.rate_limit_get_address}
                onChange={(e) => setEditForm({...editForm, rate_limit_get_address: parseInt(e.target.value)})}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Bulk CSV Rate Limit:</label>
              <input
                type="number"
                value={editForm.rate_limit_bulk_csv}
                onChange={(e) => setEditForm({...editForm, rate_limit_bulk_csv: parseInt(e.target.value)})}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Usage History Rate Limit:</label>
              <input
                type="number"
                value={editForm.rate_limit_usage_history}
                onChange={(e) => setEditForm({...editForm, rate_limit_usage_history: parseInt(e.target.value)})}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={editForm.is_admin}
                  onChange={(e) => setEditForm({...editForm, is_admin: e.target.checked})}
                  style={{ marginRight: '8px' }}
                />
                Admin Access
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleSaveUser}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Save Changes
              </button>
              <button
                onClick={() => {
                  setEditingUser(null);
                  setEditForm({});
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3>Create New User</h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Email:</label>
              <input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                placeholder="user@example.com"
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Password:</label>
              <input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                placeholder="Enter password"
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Confirm Password:</label>
              <input
                type="password"
                value={createForm.confirmPassword}
                onChange={(e) => setCreateForm({...createForm, confirmPassword: e.target.value})}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                placeholder="Confirm password"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={createForm.is_admin}
                  onChange={(e) => setCreateForm({...createForm, is_admin: e.target.checked})}
                  style={{ marginRight: '8px' }}
                />
                Admin Access
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleCreateUser}
                disabled={createLoading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: createLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {createLoading ? 'Creating...' : 'Create User'}
              </button>
              <button
                onClick={() => {
                  setShowCreateUser(false);
                  setCreateForm({
                    email: '',
                    password: '',
                    confirmPassword: '',
                    is_admin: false
                  });
                  setError('');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers; 